// Client-side model descriptor. Shape mirrors gateway ClientSafeModel (types.ts)
// exactly — supportsEffort is REQUIRED because the gateway always sends it.
// The pre-connect seed (AVAILABLE_MODELS below) must also populate it.
export interface ModelEntry {
  id: string;
  label: string;
  provider: "anthropic" | "openai" | "google" | "moonshot" | "deepseek" | "x-ai" | "qwen";
  supportsReasoning: boolean;
  nativeReasoning?: boolean;
  /** Whether the effort pill is shown for this model. Always set — sent from
   *  the gateway registry in the init frame; explicitly provided in the seed. */
  supportsEffort: boolean;
  minTier: "creator" | "vibecoder";
}

/**
 * Resolve the label shown by every model surface.
 *
 * `serverModel` is the last model acknowledged by init/reconnect/ready. Local
 * config may already contain a requested model while its confirmation dialog or
 * provider swap is still in flight, so it is only the cold-render fallback.
 */
export function resolveDisplayedModel(
  serverModel: string | null,
  availableModels: ModelEntry[],
  localFallback: ModelEntry,
): ModelEntry {
  if (!serverModel) return localFallback;
  return availableModels.find((model) => model.id === serverModel) ?? localFallback;
}

// Pre-connect fallback seed — used by the picker BEFORE the gateway's init
// frame arrives. After init, useChatConfig replaces this with the live
// registry from the server (msg.models). The authoritative list lives in
// src/gateway/model-registry.ts; this is only a best-effort approximation.
//
// supportsReasoning = true  → UI thinking toggle is shown.
// nativeReasoning = true    → thinking is auto-enabled for always-on reasoning models.
// supportsEffort = true     → effort pill is shown (required field — must be set).
//
// Only list models the gateway currently serves. OpenAI/Codex rows return when
// Phase 3 ships the Codex adapter and the gateway advertises them via init.
//
// SYNC: keep the top entry aligned with gateway/model-registry.ts DEFAULT_MODEL_ID.
// The seed is a best-effort hint; the live registry (init frame) is authoritative.
export const AVAILABLE_MODELS: ModelEntry[] = [
  // ─── Rook — Claude Max subscription ──────────────────────────────────
  { id: "claude-opus-5", label: "Opus 5 · 1M", provider: "anthropic", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
  { id: "claude-fable-5", label: "Fable 5", provider: "anthropic", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
  { id: "claude-opus-4-8", label: "Opus 4.8 · 1M", provider: "anthropic", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
  { id: "claude-opus-4-7", label: "Opus 4.7 · 1M", provider: "anthropic", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
  { id: "claude-opus-4-6", label: "Opus 4.6", provider: "anthropic", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
  { id: "claude-sonnet-4-6", label: "Sonnet 4.6", provider: "anthropic", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
  { id: "claude-haiku-4-5", label: "Haiku 4.5", provider: "anthropic", supportsReasoning: false, supportsEffort: true, minTier: "creator" },
];

export const MODEL_IDS = new Set(AVAILABLE_MODELS.map((m) => m.id));

// SYNC: EffortLevel must match in gateway/types.ts and client/shared/chat-config.ts
export type EffortLevel = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

// Pre-connect seed model. Must match a model the gateway currently serves.
// After the init frame arrives, useChatConfig uses the live registry exclusively
// and never reads this constant as a reset target (see useChatConfig.ts).
// SYNC: must match gateway/model-registry.ts DEFAULT_MODEL_ID.
export const DEFAULT_MODEL_ID = "claude-opus-4-8";

export interface ChatConfig {
  model: string;
  effort: EffortLevel;
  thinking: boolean;
  autoSummarize: boolean;
}

export const DEFAULT_CHAT_CONFIG: ChatConfig = {
  model: DEFAULT_MODEL_ID,
  effort: 'medium',
  thinking: false,
  autoSummarize: false,
};
